import Bugsnag from "@bugsnag/js";
import produce from "immer";
import noop from "lodash/noop";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import CartFreeShippingBanner from "@Components/atoms/cart/CartFreeShippingBanner";
import EstimatedDelivery from "@Components/atoms/EstimatedDelivery";
import InputSmily from "@Components/atoms/InputSmily";
import BottomSheet from "@Components/molecules/BottomSheet";
import CartArticle from "@Components/molecules/cart/CartArticle";
import CartArticleBottomSheet from "@Components/molecules/cart/CartArticleBottomSheet";
import CartPromoCodesList from "@Components/molecules/cart/CartPromoCodesList";
import PurchaseSummary from "@Components/molecules/cart/PurchaseSummary";
import Panel from "@Components/molecules/Panel";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import useCartContext from "@Hooks/useCartContext";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import { ReactComponent as IconPlus } from "@Icons/16x16/plus-circle.svg";
import CheerzApiCart from "@Types/api/cart";
import { ApiErrorResponse, ApiV2ErrorResponse } from "@Types/api/error";
import { Article, Step } from "@Types/cart";
import { PriceFormatted } from "@Types/price";
import PromoContent from "@Types/promos";
import {
  convertCartArticleFromApiToContent,
  convertCartFromApiToContent,
  convertCartFromContentToApi,
} from "@Utils/cart/cart";
import { apiV2ErrorManager } from "@Utils/error";
import { goToCategories } from "@Utils/navigation";
import {
  convertToCheckoutStepProperties,
  convertToOrderUpdatedProperties,
  setDataLayerTrack,
} from "@Utils/track";
import "./style.scss";

interface Props {
  articles: Article[];
  disabled: boolean;
  displayFreeShippingBanner: boolean;
  expressDeliveryDates: string[] | undefined;
  onConfirmOrder: () => void;
  onCopy: (locale: string, id: number, callback: () => void) => void;
  onUpdatePromoCode: (promoCode: string | undefined) => Promise<void>;
  promoCode: string | undefined;
  promoCodeErrorMessage: string | undefined;
  promos: PromoContent[];
  promoTotal: PriceFormatted;
  shippingTotal: PriceFormatted;
  standardDeliveryDates: string[] | undefined;
  subTotal: PriceFormatted;
  total: PriceFormatted;
}

interface ContainerProps {
  onChangeStep: (step: Step) => void;
  onUpdatePromoCode: (promoCode: string | undefined) => Promise<void>;
  promoCodeErrorMessage: string | undefined;
}

export const CartCheckoutStep: FunctionComponent<Props> = ({
  articles,
  disabled,
  displayFreeShippingBanner,
  expressDeliveryDates,
  onConfirmOrder,
  onCopy,
  onUpdatePromoCode,
  promoCode,
  promoCodeErrorMessage,
  promos,
  promoTotal,
  shippingTotal,
  standardDeliveryDates,
  subTotal,
  total,
}) => {
  const { isLargeScreen, isSmallScreen } = useBreakpoint();
  const intl = useIntl();
  const purchaseSummary = useRef<HTMLDivElement>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<number>();
  const [displayBottomSheet, setDisplayBottomSheet] = useState<boolean>(false);
  const [displayPromoCodesList, setDisplayPromoCodesList] = useState<boolean>(false);
  const [displayStickyButton, setDisplayStickyButton] = useState<boolean>(false);
  const [displayContent, setDisplayContent] = useState<boolean>(false);
  const [promoCodeInput, setPromoCodeInput] = useState<string>("");

  const selectedArticle = articles.find((article) => article.id === selectedArticleId);

  const handleClickCategoriesLink = () => {
    setDataLayerTrack("Product List Clicked", {
      categoryPageTag: "all",
      linkPosition: "Checkout",
    });
    goToCategories(intl.locale, "all");
  };

  const handleClickPromoCodesList = () => {
    setDataLayerTrack("Cart Promo Codes List Clicked");
    setDisplayPromoCodesList(true);
  };

  const handleDismissBottomSheet = () => {
    setDisplayBottomSheet(false);
    document.body.classList.toggle("noScroll", false);
  };

  const handleDisplayBottomSheet = (id: number) => {
    setSelectedArticleId(id);
    setDisplayBottomSheet(true);
  };

  const handleValidatePromoCode = (code: string | undefined) =>
    onUpdatePromoCode(code)
      .then(() => {
        setDisplayContent(false);
        setDisplayPromoCodesList(false);
      })
      .catch((message?: string) => {
        Bugsnag.notify({
          message: !!message ? message : "onUpdatePromoCode function",
          name: "Error promo code",
        });
        setDisplayPromoCodesList(false);
      });

  const hasOutOfStockProduct = (): boolean =>
    articles.some((article) => article.product.outOfStock === true);

  useEffect(() => {
    if (purchaseSummary.current) {
      const initialCheckoutTotalTopPosition = purchaseSummary.current.getBoundingClientRect().top;
      const isCheckoutButtonVisible =
        window.innerHeight - initialCheckoutTotalTopPosition >=
        purchaseSummary.current.clientHeight;
      if (isSmallScreen && !isCheckoutButtonVisible) {
        setDisplayStickyButton(true);
        const handleScroll = () => {
          if (
            purchaseSummary.current &&
            document.documentElement.scrollTop <=
              purchaseSummary.current.clientHeight -
                (window.innerHeight - initialCheckoutTotalTopPosition) -
                16 // 16 is the difference of padding between the stickyButton and the CartCheckoutTotal button
          ) {
            setDisplayStickyButton(true);
          } else {
            setDisplayStickyButton(false);
          }
        };
        window.addEventListener("scroll", handleScroll);

        return function cleanup() {
          window.removeEventListener("scroll", handleScroll);
        };
      }
    }
  }, []);

  useEffect(() => {
    promoCode && setPromoCodeInput("");
  }, [promoCode]);

  const promoCodesList = (
    <CartPromoCodesList
      onClickPromo={(code: string) => handleValidatePromoCode(code)}
      promos={promos}
    />
  );

  return (
    <div className="CartCheckoutStep">
      {displayFreeShippingBanner && (
        <CartFreeShippingBanner
          onUpdatePromoCode={(promoCode) => handleValidatePromoCode(promoCode)}
        />
      )}
      <div className="CartCheckoutStep-content">
        <div className="CartCheckoutStep-content-cart">
          {isLargeScreen && (
            <div className="CartCheckoutStep-title">
              {intl.formatMessage({ id: "cheerz_HeaderMobileCart_title" })}
            </div>
          )}

          {articles.map((article) => (
            <CartArticle
              alternativeDesign={false}
              article={article}
              key={article.id}
              onClickArticle={isLargeScreen ? noop : handleDisplayBottomSheet}
              onCopy={(locale, id) => onCopy(locale, id, () => handleDismissBottomSheet())}
            />
          ))}

          <ButtonSmily
            disabled={disabled}
            icon={<IconPlus />}
            iconPosition="left"
            onClick={handleClickCategoriesLink}
            size="m"
            type="tertiary"
          >
            {intl.formatMessage({ id: "cheerz_CartCheckoutStep_new_product" })}
          </ButtonSmily>
          {!!standardDeliveryDates && standardDeliveryDates[0] && standardDeliveryDates[1] && (
            <EstimatedDelivery
              expressDeliveryDates={expressDeliveryDates}
              standardDeliveryDates={standardDeliveryDates}
            />
          )}
        </div>

        <div ref={purchaseSummary}>
          <PurchaseSummary
            contentType="promoCode"
            disabled={disabled}
            displayContent={displayContent}
            estimatedShippingCost={true}
            hasOutOfStockProduct={hasOutOfStockProduct()}
            isFreeShippingBannerDisplayed={displayFreeShippingBanner}
            onClickButton={onConfirmOrder}
            onClickTitle={() => setDisplayContent(!displayContent)}
            onRemovePromoCode={() => handleValidatePromoCode(undefined)}
            promoCode={promoCode}
            promoTotal={promoTotal}
            shippingTotal={shippingTotal}
            subTotal={subTotal}
            total={total}
          >
            <div
              className="CartCheckoutStep-promoCode"
              style={{
                maxHeight: displayContent ? "200px" : 0,
              }}
            >
              {intl.formatMessage({ id: "cheerz_CartCheckoutStep_promoCode_info" })}
              <span className="CartCheckoutStep-promoCode-link" onClick={handleClickPromoCodesList}>
                {intl.formatMessage({ id: "cheerz_CartCheckoutStep_promoCode_list" })}
              </span>
              <InputSmily
                disabled={disabled}
                errorMessage={promoCodeErrorMessage}
                onChange={(value) => setPromoCodeInput(value)}
                onDismiss={() => setPromoCodeInput("")}
                onValidate={() => handleValidatePromoCode(promoCodeInput.toUpperCase())}
                placeholder={intl.formatMessage({ id: "cheerz_CartCheckoutStep_promoCode_input" })}
                type="text"
                value={promoCodeInput}
              />
              <ButtonSmily
                disabled={disabled || promoCodeInput === ""}
                onClick={() => handleValidatePromoCode(promoCodeInput.toUpperCase())}
                size="s"
                type="secondary"
              >
                {intl.formatMessage({ id: "cheerz_CartFreeShippingBanner_apply" })}
              </ButtonSmily>
            </div>
          </PurchaseSummary>
        </div>
      </div>

      {!!selectedArticle && (
        <CartArticleBottomSheet
          article={selectedArticle}
          display={displayBottomSheet}
          onCopy={(locale, id) => onCopy(locale, id, () => handleDismissBottomSheet())}
          onDismiss={handleDismissBottomSheet}
        />
      )}

      {isLargeScreen ? (
        <Panel display={displayPromoCodesList} onDismiss={() => setDisplayPromoCodesList(false)}>
          {promoCodesList}
        </Panel>
      ) : (
        <BottomSheet
          display={displayPromoCodesList}
          onDismiss={() => setDisplayPromoCodesList(false)}
          showCloseButton={true}
        >
          {promoCodesList}
        </BottomSheet>
      )}

      {displayStickyButton && (
        <div
          className="CartCheckoutStep-stickyButton"
          style={{
            width: `${purchaseSummary.current ? purchaseSummary.current.clientWidth : 0}px`,
          }}
        >
          <ButtonSmily
            disabled={disabled || hasOutOfStockProduct()}
            onClick={onConfirmOrder}
            size="m"
            type="primary"
          >
            {intl.formatMessage({ id: "cheerz_PurchaseSummary_checkout" })}
          </ButtonSmily>
        </div>
      )}
    </div>
  );
};

const CartCheckoutStepContainer: FunctionComponent<ContainerProps> = ({
  onChangeStep,
  onUpdatePromoCode,
  promoCodeErrorMessage,
}) => {
  const { cart, configuration, dispatch, promos, ui } = useCartContext();
  const { dispatchNotification } = useNotificationsContext();

  useEffect(() => {
    setDataLayerTrack("Checkout Step Viewed", convertToCheckoutStepProperties("Checkout", 1, cart));
  }, []);

  const handleCopy = (locale: string, id: number, callback: () => void) => {
    dispatch({
      disabled: true,
      type: "setDisabledCart",
    });

    if (configuration.apiProviders.cheerzV2) {
      configuration.apiProviders.cheerzV2
        .copyArticle(locale, id)
        .then(({ response }) => {
          const updatedCart = produce(cart, (draftState) => {
            draftState.articles.unshift(...convertCartArticleFromApiToContent([response]));
          });
          dispatch({
            cart: updatedCart,
            type: "setSavedCart",
          });
          setDataLayerTrack(
            "Order Updated",
            convertToOrderUpdatedProperties(updatedCart, "Checkout")
          );
          callback();
        })
        .catch((error: ApiV2ErrorResponse) => {
          apiV2ErrorManager(error, dispatchNotification);
          dispatch({
            disabled: false,
            type: "setDisabledCart",
          });
        });
    }
  };

  const handleSubmitOrder = () => {
    if (configuration) {
      dispatch({
        disabled: true,
        type: "setDisabledCart",
      });

      const updatedCartJson = convertCartFromContentToApi(cart);

      configuration.apiProviders.cheerz
        .saveCart(updatedCartJson)
        .then((cartResponse: CheerzApiCart) => {
          dispatch({
            cart: {
              ...convertCartFromApiToContent(cartResponse),
            },
            type: "setSavedCart",
          });
          setDataLayerTrack(
            "Checkout Step Completed",
            convertToCheckoutStepProperties("Checkout", 1, cart)
          );
          onChangeStep("address");
        })
        .catch((error: ApiErrorResponse) => {
          dispatch({
            disabled: false,
            type: "setDisabledCart",
          });
          Bugsnag.notify(
            Error(
              error.response?.data?.error_description[0]
                ? `${error.response?.data?.error_description[0]}`
                : "Error on saveCart call"
            )
          );
        });
    }
  };

  return (
    <CartCheckoutStep
      articles={cart.articles}
      disabled={ui.disabled}
      displayFreeShippingBanner={!cart.promoCode && !!cart.freeShippingConfig}
      expressDeliveryDates={cart.shippingOptions.home.express.deliveryDates}
      onConfirmOrder={handleSubmitOrder}
      onCopy={handleCopy}
      onUpdatePromoCode={onUpdatePromoCode}
      promoCode={cart.promoCode}
      promoCodeErrorMessage={promoCodeErrorMessage}
      promos={promos}
      promoTotal={cart.promoTotal}
      shippingTotal={cart.shippingTotal}
      standardDeliveryDates={cart.shippingOptions.home.normal.deliveryDates}
      subTotal={cart.subTotal}
      total={cart.total}
    />
  );
};

export default CartCheckoutStepContainer;
